import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type {
  DashboardErrorRow,
  DashboardPeriod,
  DashboardSummary,
  DashboardFlowRow,
  DashboardSystemRow,
} from '../api/contracts';
import { useMonitorApi } from '../app/api-context';
import { ErrorBanner } from '../components/ErrorBanner';
import { EmptyState, LoadingBlock } from '../components/Feedback';
import { IconCheckCircle, IconChevronRight } from '../components/icons';
import { useAsyncResource } from '../hooks/use-async-resource';
import { flowLabel, systemLabel } from '../lib/catalogs';
import { formatDateTime } from '../lib/format';

/**
 * Tablero de operacion.
 *
 * ORDEN DE LECTURA, de arriba hacia abajo: que hay que atender AHORA, que tan
 * confiable viene el periodo, y recien despues el detalle. Es el orden en que se
 * necesita: quien abre esta pantalla un lunes a la mañana quiere saber si tiene
 * un problema antes que cuantos mensajes se movieron.
 *
 * LO QUE ESTE TABLERO NO MUESTRA: pesos. El middleware mueve mensajes y no
 * conoce montos —los flujos de pedidos y ventas todavia no corren—. Un panel que
 * dijera "impacto" con estos datos estaria inventandolo.
 *
 * Todo el estilo vive en global.css (`.metric*`, `.daychart*`, `.dashboard*`):
 * el tono de cada numero es una clase, asi sigue al tema claro u oscuro.
 */

const PERIOD_LABELS: Record<DashboardPeriod, string> = {
  '24h': 'Últimas 24 horas',
  '7d': 'Últimos 7 días',
  '30d': 'Últimos 30 días',
};

type Tone = 'neutral' | 'ok' | 'warn' | 'danger';

const countFormatter = new Intl.NumberFormat('es-CL');

export function DashboardPage(): ReactElement {
  const api = useMonitorApi();
  const [period, setPeriod] = useState<DashboardPeriod>('7d');
  const resource = useAsyncResource(() => api.getDashboard(period), [api, period]);
  const data = resource.data;
  // Al cambiar de periodo se conserva lo anterior, atenuado, hasta que llega lo
  // nuevo: sin saltos de layout ni un "cargando" que borre la pantalla.
  const refreshing = resource.loading && data !== null;

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Tablero de operación</h1>
          <p className="page__subtitle">
            {data === null
              ? PERIOD_LABELS[period]
              : `${PERIOD_LABELS[period]} · ${formatDateTime(data.from)} a ${formatDateTime(data.to)}`}
          </p>
        </div>
        <div className="page__actions">
          <label>
            <span className="visually-hidden">Periodo</span>
            <select
              className="select select--auto"
              value={period}
              onChange={(event) => setPeriod(event.target.value as DashboardPeriod)}
            >
              {(Object.keys(PERIOD_LABELS) as DashboardPeriod[]).map((value) => (
                <option key={value} value={value}>
                  {PERIOD_LABELS[value]}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="button" onClick={resource.reload}>
            Actualizar
          </button>
        </div>
      </header>

      <ErrorBanner error={resource.error} onRetry={resource.reload} />

      {resource.loading && data === null ? (
        <LoadingBlock label="Calculando el tablero…" />
      ) : data === null ? null : (
        <div
          className={refreshing ? 'dashboard dashboard--refreshing' : 'dashboard'}
          aria-busy={refreshing}
        >
          <Backlog data={data} />
          <Reliability data={data} />
          <Volume data={data} />
          <Breakdown data={data} />
          <TopErrors errors={data.topErrors} />
        </div>
      )}
    </div>
  );
}

/** Lo primero: lo que espera que alguien haga algo. */
function Backlog({ data }: { data: DashboardSummary }): ReactElement {
  const { backlog } = data;
  const pendiente = backlog.deadLettersPending + backlog.quarantinePending + backlog.stuck;

  return (
    <section className="dashboard__section" aria-labelledby="tablero-atencion">
      <header className="section-header">
        <h2 id="tablero-atencion">Requiere atención</h2>
        <p className="section-header__note">
          No se recorta por el periodo: lo que sigue sin resolver cuenta aunque sea viejo.
        </p>
      </header>
      {pendiente === 0 ? (
        <div className="all-clear">
          <IconCheckCircle size={22} className="all-clear__icon" />
          <div>
            <p className="all-clear__title">Nada pendiente.</p>
            <p className="all-clear__hint">
              Sin dead letters ni cuarentena sin resolver, y ningún mensaje atascado.
            </p>
          </div>
        </div>
      ) : (
        <div className="metric-grid">
          <Metric
            label="Dead letters"
            value={backlog.deadLettersPending}
            tone={backlog.deadLettersPending > 0 ? 'danger' : 'neutral'}
            hint={
              backlog.oldestDeadLetterAt === null
                ? undefined
                : `la más vieja: ${formatDateTime(backlog.oldestDeadLetterAt)}`
            }
            to="/dead-letters"
          />
          <Metric
            label="En cuarentena"
            value={backlog.quarantinePending}
            tone={backlog.quarantinePending > 0 ? 'warn' : 'neutral'}
            hint={
              backlog.oldestQuarantineAt === null
                ? undefined
                : `la más vieja: ${formatDateTime(backlog.oldestQuarantineAt)}`
            }
            to="/cuarentena"
          />
          <Metric
            label="Atascados"
            value={backlog.stuck}
            tone={backlog.stuck > 0 ? 'warn' : 'neutral'}
            hint={`en vuelo hace más de ${backlog.stuckThresholdMinutes} min`}
          />
        </div>
      )}
    </section>
  );
}

/** Que tan confiable viene el periodo, contra el anterior. */
function Reliability({ data }: { data: DashboardSummary }): ReactElement {
  const { health, latency } = data;
  const delta =
    health.successRate === null || health.previousSuccessRate === null
      ? null
      : health.successRate - health.previousSuccessRate;

  return (
    <section className="dashboard__section" aria-labelledby="tablero-confiabilidad">
      <header className="section-header">
        <h2 id="tablero-confiabilidad">Confiabilidad</h2>
      </header>
      <div className="metric-grid">
        <Metric
          label="Mensajes procesados sin error"
          value={health.successRate === null ? '—' : formatPercent(health.successRate)}
          tone={toneForRate(health.successRate)}
          hint={
            delta === null
              ? 'sin periodo anterior para comparar'
              : `${delta >= 0 ? '▲' : '▼'} ${formatPercent(Math.abs(delta))} vs. periodo anterior`
          }
          // Subir es bueno y bajar es malo: el color de la variacion lo dice.
          hintTone={delta === null || delta === 0 ? undefined : delta > 0 ? 'ok' : 'danger'}
        />
        <Metric
          label="Con error"
          value={health.counts.failed}
          tone={health.counts.failed > 0 ? 'danger' : 'neutral'}
        />
        <Metric
          label="En cuarentena"
          value={health.counts.quarantined}
          tone={health.counts.quarantined > 0 ? 'warn' : 'neutral'}
        />
        {latency === null ? null : (
          <>
            <Metric label="Mediana de proceso" value={formatMs(latency.p50Ms)} />
            <Metric
              label="Percentil 95"
              value={formatMs(latency.p95Ms)}
              hint="19 de cada 20 mensajes tardan menos que esto"
            />
          </>
        )}
      </div>
    </section>
  );
}

/** Cuanto se movio, dia a dia. */
function Volume({ data }: { data: DashboardSummary }): ReactElement {
  const { health, byDay } = data;

  return (
    <section className="panel" aria-labelledby="tablero-volumen">
      <header className="panel__header">
        <h2 id="tablero-volumen">Volumen</h2>
        <span className="panel__meta">
          {formatCount(health.counts.total)} mensajes · {formatCount(health.previousTotal)} en el
          periodo anterior
        </span>
      </header>
      <div className="panel__body">
        {byDay.length === 0 ? (
          <EmptyState title="Sin mensajes en el periodo." />
        ) : (
          <DailyVolume rows={byDay} />
        )}
      </div>
    </section>
  );
}

/**
 * Serie diaria en columnas apiladas: lo que paso sin error y, pegado a la base,
 * lo que fallo.
 *
 * Es HTML y CSS a mano y no una libreria de graficos: son dos series y una
 * escala. Una dependencia de charts agregaria peso al bundle y otra forma de
 * tematizar que habria que mantener alineada con el resto. La tabla de abajo es
 * su gemela: los mismos numeros, legibles sin pasar el mouse.
 */
function DailyVolume({ rows }: { rows: DashboardSummary['byDay'] }): ReactElement {
  const top = niceCeiling(Math.max(...rows.map((row) => row.total), 1));
  // Con muchos dias no caben todas las fechas: se rotula una de cada tantas.
  const labelEvery = rows.length > 16 ? 5 : rows.length > 10 ? 2 : 1;
  const total = rows.reduce((sum, row) => sum + row.total, 0);
  const failed = rows.reduce((sum, row) => sum + row.failed, 0);

  return (
    <figure className="daychart">
      <div className="daychart__legend" aria-hidden="true">
        <span>
          <i className="daychart__key daychart__key--ok" />
          Sin error
        </span>
        <span>
          <i className="daychart__key daychart__key--failed" />
          Con error
        </span>
      </div>

      <div
        className="daychart__plot"
        role="img"
        aria-label={`Mensajes por día: ${formatCount(total)} en ${rows.length} días, ${formatCount(failed)} con error.`}
      >
        <div className="daychart__axis" aria-hidden="true">
          <span className="daychart__tick daychart__tick--max">{formatCount(top)}</span>
          <span className="daychart__tick daychart__tick--mid">{formatCount(top / 2)}</span>
          <span className="daychart__tick daychart__tick--zero">0</span>
        </div>
        <div className="daychart__bars">
          {rows.map((row, index) => {
            const ok = row.total - row.failed;
            return (
              <div
                key={row.date}
                className="daychart__day"
                title={`${formatDay(row.date)}: ${formatCount(row.total)} mensajes, ${formatCount(row.failed)} con error`}
              >
                <div className="daychart__track">
                  <div
                    className="daychart__stack"
                    style={{ height: `${(row.total / top) * 100}%` }}
                  >
                    {ok > 0 ? (
                      <span className="daychart__seg daychart__seg--ok" style={{ flexGrow: ok }} />
                    ) : null}
                    {row.failed > 0 ? (
                      <span
                        className="daychart__seg daychart__seg--failed"
                        style={{ flexGrow: row.failed }}
                      />
                    ) : null}
                  </div>
                </div>
                <span className="daychart__label">
                  {index % labelEvery === 0 || index === rows.length - 1 ? row.date.slice(8) : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <details className="daychart__data">
        <summary>Ver los datos por día</summary>
        <div className="table__wrap">
          <table className="table">
            <caption className="visually-hidden">Mensajes por día</caption>
            <thead>
              <tr>
                <th scope="col">Día</th>
                <th scope="col" className="num">
                  Mensajes
                </th>
                <th scope="col" className="num">
                  Con error
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.date}>
                  <td className="nowrap">{formatDay(row.date)}</td>
                  <td className="num">{formatCount(row.total)}</td>
                  <td className="num">{formatCount(row.failed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}

/** Detalle por flujo y por sistema, lado a lado cuando hay ancho. */
function Breakdown({ data }: { data: DashboardSummary }): ReactElement {
  return (
    <div className="dashboard__split">
      <section className="panel" aria-labelledby="tablero-por-flujo">
        <header className="panel__header">
          <h2 id="tablero-por-flujo">Por flujo</h2>
        </header>
        {data.byFlow.length === 0 ? (
          <EmptyState title="Sin actividad en el periodo." />
        ) : (
          <BreakdownTable
            nameLabel="Flujo"
            rows={data.byFlow.map((row: DashboardFlowRow) => ({
              key: row.flow ?? 'sin-flujo',
              label: flowLabel(row.flow),
              counts: row.counts,
              successRate: row.successRate,
              lastActivityAt: row.lastActivityAt,
            }))}
          />
        )}
      </section>

      <section className="panel" aria-labelledby="tablero-por-sistema">
        <header className="panel__header">
          <h2 id="tablero-por-sistema">Por sistema</h2>
        </header>
        {data.bySystem.length === 0 ? (
          <EmptyState title="Sin actividad en el periodo." />
        ) : (
          <BreakdownTable
            nameLabel="Sistema"
            rows={data.bySystem.map((row: DashboardSystemRow) => ({
              key: row.system,
              label: systemLabel(row.system),
              counts: row.counts,
              successRate: row.successRate,
              lastActivityAt: row.lastActivityAt,
            }))}
          />
        )}
      </section>
    </div>
  );
}

interface BreakdownRow {
  readonly key: string;
  readonly label: string;
  readonly counts: DashboardSummary['health']['counts'];
  readonly successRate: number | null;
  readonly lastActivityAt: string | null;
}

function BreakdownTable({
  nameLabel,
  rows,
}: {
  nameLabel: string;
  rows: readonly BreakdownRow[];
}): ReactElement {
  return (
    <div className="table__wrap">
      <table className="table table--stack">
        <thead>
          <tr>
            <th scope="col">{nameLabel}</th>
            <th scope="col" className="num">
              Mensajes
            </th>
            <th scope="col" className="num">
              Con error
            </th>
            <th scope="col">Éxito</th>
            <th scope="col">Última actividad</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="table__lead">{row.label}</td>
              <td className="num" data-label="Mensajes">
                {formatCount(row.counts.total)}
              </td>
              <td
                className={row.counts.failed > 0 ? 'num num--danger' : 'num'}
                data-label="Con error"
              >
                {formatCount(row.counts.failed)}
              </td>
              <td className="nowrap" data-label="Éxito">
                {row.successRate === null ? (
                  '—'
                ) : (
                  <span className={`rate rate--${toneForRate(row.successRate)}`}>
                    {formatPercent(row.successRate)}
                  </span>
                )}
              </td>
              <td className="muted nowrap" data-label="Última actividad">
                {row.lastActivityAt === null ? '—' : formatDateTime(row.lastActivityAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Los errores que mas se repiten, con su evidencia a un clic. */
function TopErrors({ errors }: { errors: readonly DashboardErrorRow[] }): ReactElement {
  return (
    <section className="panel" aria-labelledby="tablero-errores">
      <header className="panel__header">
        <h2 id="tablero-errores">Errores más repetidos</h2>
        <span className="panel__meta">Agrupados por el comienzo del mensaje de error</span>
      </header>
      {errors.length === 0 ? (
        <EmptyState title="Ningún error en el periodo." />
      ) : (
        <div className="table__wrap">
          <table className="table table--stack">
            <thead>
              <tr>
                <th scope="col">Error</th>
                <th scope="col" className="num">
                  Veces
                </th>
                <th scope="col">Sistema</th>
                <th scope="col">Flujo</th>
                <th scope="col">Última vez</th>
                <th scope="col">
                  <span className="visually-hidden">Caso</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {errors.map((row) => (
                <tr key={`${row.system}:${row.message}`}>
                  <td className="mono table__message table__lead">{row.message}</td>
                  <td className="num" data-label="Veces">
                    {formatCount(row.count)}
                  </td>
                  <td className="nowrap" data-label="Sistema">
                    {systemLabel(row.system)}
                  </td>
                  <td data-label="Flujo">{flowLabel(row.flow)}</td>
                  <td className="muted nowrap" data-label="Última vez">
                    {formatDateTime(row.lastSeenAt)}
                  </td>
                  <td className="nowrap">
                    <Link to={`/mensajes/${row.sampleMessageId}`}>ver un caso</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Tarjeta de un numero grande. Con `to`, la tarjeta entera lleva a la pantalla. */
function Metric({
  label,
  value,
  hint,
  hintTone,
  tone = 'neutral',
  to,
}: {
  label: string;
  value: string | number;
  hint?: string | undefined;
  hintTone?: Tone | undefined;
  tone?: Tone;
  to?: string | undefined;
}): ReactElement {
  const className = `metric metric--${tone}${to === undefined ? '' : ' metric--link'}`;
  const body = (
    <>
      <div className="metric__label">{label}</div>
      <div className="metric__value">{typeof value === 'number' ? formatCount(value) : value}</div>
      {hint === undefined ? null : (
        <div
          className={
            hintTone === undefined ? 'metric__hint' : `metric__hint metric__hint--${hintTone}`
          }
        >
          {hint}
        </div>
      )}
      {to === undefined ? null : <IconChevronRight size={16} className="metric__go" />}
    </>
  );

  return to === undefined ? (
    <div className={className}>{body}</div>
  ) : (
    <Link className={className} to={to}>
      {body}
    </Link>
  );
}

/**
 * Umbrales del semaforo.
 *
 * Son deliberadamente exigentes: un 5% de mensajes fallando es, en un
 * middleware de integraciones, un pedido de cada veinte que no llego a destino.
 */
function toneForRate(rate: number | null): Tone {
  if (rate === null) {
    return 'neutral';
  }
  if (rate >= 0.99) {
    return 'ok';
  }
  return rate >= 0.95 ? 'warn' : 'danger';
}

/**
 * Techo "redondo" de la escala: 1, 2, 4, 6, 8 o 10 por una potencia de diez.
 * Nunca impar bajo 10, para que la marca de la mitad tambien sea un entero.
 */
function niceCeiling(value: number): number {
  if (value <= 2) {
    return 2;
  }
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 4, 6, 8, 10].find((candidate) => candidate * magnitude >= value) ?? 10;
  return step * magnitude;
}

function formatCount(value: number): string {
  return countFormatter.format(value);
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function formatMs(value: number): string {
  return value < 1000 ? `${value} ms` : `${(value / 1000).toFixed(1)} s`;
}

/** `2026-09-17` -> `17-09-2026`, el mismo orden que el resto de las fechas. */
function formatDay(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  return year === undefined || month === undefined || day === undefined
    ? isoDate
    : `${day}-${month}-${year}`;
}
